import React, { useMemo } from 'react';
import { useData } from '../../store/DataContext';
import { ArrowDownIcon, ArrowUpIcon, CircleDollarSignIcon, PackageCheckIcon } from 'lucide-react';
import { formatCurrency, isToday } from './pharmacyUtils';
export function InventoryReports() {
  const {
    medicines,
    stockMovements
  } = useData();
  const stats = useMemo(() => {
    const today = stockMovements.filter(movement => isToday(movement.createdAt));
    const inbound = today.filter(movement => movement.quantityDelta > 0).reduce((sum, movement) => sum + movement.quantityDelta, 0);
    const outbound = today.filter(movement => movement.quantityDelta < 0).reduce((sum, movement) => sum + Math.abs(movement.quantityDelta), 0);
    const dispensed = today.filter(movement => movement.type === 'Dispense').length;
    const expired = stockMovements.filter(movement => movement.type === 'Expired Disposal').reduce((sum, movement) => sum + Math.abs(movement.quantityDelta), 0);
    return {
      inbound,
      outbound,
      dispensed,
      expired,
      value: medicines.reduce((sum, medicine) => sum + medicine.currentQuantity * medicine.costPrice, 0)
    };
  }, [medicines, stockMovements]);
  const tiles = [{
    label: 'Inventory value',
    value: formatCurrency(stats.value),
    icon: CircleDollarSignIcon,
    tone: 'blue'
  }, {
    label: 'Stock received today',
    value: stats.inbound,
    icon: ArrowUpIcon,
    tone: 'emerald'
  }, {
    label: 'Stock issued today',
    value: stats.outbound,
    icon: ArrowDownIcon,
    tone: 'amber'
  }, {
    label: 'Prescription items dispensed today',
    value: stats.dispensed,
    icon: PackageCheckIcon,
    tone: 'teal'
  }];
  return <div className="space-y-6" data-id="element-4153">
      <div data-id="element-4154">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4155">Inventory Reports</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4156">
          A live operational snapshot derived from the inventory ledger.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-4157">
        {tiles.map(({
        label,
        value,
        icon: Icon,
        tone
      }) => <article key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4158">
            <div className="flex justify-between" data-id="element-4159">
              <div data-id="element-4160">
                <p className="text-sm font-medium text-slate-500" data-id="element-4161">{label}</p>
                <p className="mt-2 text-2xl font-bold text-slate-950" data-id="element-4162">
                  {value}
                </p>
              </div>
              <div className={`rounded-lg p-2.5 ${tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' : tone === 'amber' ? 'bg-amber-50 text-amber-700' : tone === 'teal' ? 'bg-teal-50 text-teal-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-4163">
                <Icon className="h-5 w-5" data-id="element-4164" />
              </div>
            </div>
          </article>)}
      </div>
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4165">
        <h3 className="font-bold text-slate-900" data-id="element-4166">Control indicators</h3>
        <dl className="mt-4 grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0" data-id="element-4167">
          <Indicator label="Expired units disposed" value={stats.expired} detail="All-time ledger total" data-id="element-4168" />
          <Indicator label="Total recorded movements" value={stockMovements.length} detail="Receipts, issues, returns, and adjustments" data-id="element-4169" />
          <Indicator label="Average value per medicine" value={medicines.length ? formatCurrency(stats.value / medicines.length) : formatCurrency(0)} detail="Based on current cost value" data-id="element-4170" />
        </dl>
      </section>
    </div>;
}
function Indicator({
  label,
  value,
  detail
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return <div className="py-3 md:px-5 md:first:pl-0" data-id="element-4171">
      <dt className="text-sm font-medium text-slate-500" data-id="element-4172">{label}</dt>
      <dd className="mt-2 text-xl font-bold text-slate-950" data-id="element-4173">{value}</dd>
      <p className="mt-1 text-xs text-slate-500" data-id="element-4174">{detail}</p>
    </div>;
}